import React, { FC } from 'react'
import { useHistory } from 'react-router-dom'
import { t, Trans } from '@lingui/macro'

import Loader from '../../../components/common/loader/Loader'
import PathwayProgressDetail from '../../pathway-detail-page/components/pathway-progress-detail/PathwayProgressDetail'
import UserAvatar from '../../../components/common/user/user-avatar/UserAvatar'
import { AnalyticsHeader } from '../AnalyticsHeader'
import PathwayDetailOverviewTable from './PathwayDetailOverviewTable'
import { PathwayEnrolmentStatusEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import BorderedBox from 'front-end/src/components/common/bordered-box/BorderedBox'
import { formatDateAndTimeShort } from 'front-end/src/utils/dateHelpers'
import { usePathwayEnrolmentDetailAnalyticsQuery } from './pathwayDetailAnalytics.codegen'
import { useParams } from 'react-router'
import { StatusArchivedGrey } from 'front-end/src/components/common/status/Status'
import { linkBuilder } from 'shared/linkBuilder'
import { ButtonIcon, Link } from '../../../components'
import { brandColors } from 'shared/basicPalette'
import { IconNewTab } from '../../../components/common/icon/IconNewTab'

export interface IVariables {
  enrolmentId: number
}

const PathwayEnrolmentDetailAnalytics: FC<{}> = () => {
  const history = useHistory()
  const { enrolmentId } = useParams<{ enrolmentId: string }>()
  const { data, loading } = usePathwayEnrolmentDetailAnalyticsQuery({
    variables: {
      enrolmentId: +enrolmentId
    }
  })

  if (loading) {
    return <Loader />
  }

  const pathwayEnrolment = data?.pathwayEnrolment
  if (!pathwayEnrolment) {
    return null
  }

  const renderOverviewTable = () => {
    if (pathwayEnrolment.importedAt) {
      return null
    }

    if (pathwayEnrolment.deleted_at && !pathwayEnrolment.completed_at) {
      return null
    }
    return (
      <PathwayDetailOverviewTable
        topics={pathwayEnrolment?.topics ?? []}
        topicEnrolments={pathwayEnrolment?.topicEnrolments ?? []}
      />
    )
  }

  return (
    <div className="PathwaysDetailAnalyticsPage">
      <AnalyticsHeader
        goBack={history.goBack}
        showFilterPanel={false}
        className="AnalyticsHeader--detail"
      >
        <>
          <div className="AnalyticsHeader--detail__content">
            <UserAvatar size="normal" user={pathwayEnrolment.user} />
            <div className="AnalyticsHeader__title__text">
              <small>
                {pathwayEnrolment.user.display_name}{' '}
                <span className="AnalyticsHeader__title__text__subtitle">
                  {pathwayEnrolment?.status &&
                    renderHeaderStatus(pathwayEnrolment.status)}
                </span>{' '}
              </small>
              <div>
                {pathwayEnrolment.pathway?.title}
                {pathwayEnrolment.pathway?.id && (
                  <Link
                    isExternal
                    to={linkBuilder.pathwayDetailManage(
                      pathwayEnrolment.pathway?.id
                    )}
                    ml="8px"
                  >
                    <ButtonIcon
                      color={brandColors.grey['700']}
                      aria-label={t`Go to pathway`}
                      hint={t`Go to pathway`}
                      icon={<IconNewTab w="19px" h="18px" />}
                      circular
                      hintProps={{
                        placement: 'top'
                      }}
                      _hover={{
                        bgColor: 'white'
                      }}
                    />
                  </Link>
                )}
                {pathwayEnrolment.pathway?.deleted_at && (
                  <StatusArchivedGrey mx="8px" />
                )}
              </div>
            </div>
          </div>
          <div className="AnalyticsHeader--detail__status">
            <PathwayProgressDetail enrolment={pathwayEnrolment} />
          </div>
        </>
      </AnalyticsHeader>

      <div className="AnalyticsRow">
        <BorderedBox className="AnalyticsRow__box">
          <div className="Workbook__detail__wrapper">
            <div className="Workbook__detail">
              <h2>
                <Trans>Summary</Trans>
              </h2>
              <div className="Workbook__detail__info">
                {pathwayEnrolment.deleted_at && (
                  <div className="Workbook__detail__item">
                    <span className="Workbook__label">
                      <Trans>Archived</Trans>
                    </span>
                    <span>
                      {formatDateAndTimeShort(pathwayEnrolment?.deleted_at)}
                    </span>
                  </div>
                )}
                {!pathwayEnrolment.importRecord && (
                  <div className="Workbook__detail__item">
                    <span className="Workbook__label">
                      <Trans>Version</Trans>
                    </span>
                    <span>{pathwayEnrolment.pathwayVersion?.version}</span>
                  </div>
                )}
                <div className="Workbook__detail__item">
                  <span className="Workbook__label">
                    <Trans>Started</Trans>
                  </span>
                  <span>
                    {formatDateAndTimeShort(pathwayEnrolment.started_at)}
                  </span>
                </div>
                <div className="Workbook__detail__item">
                  <span className="Workbook__label">
                    <Trans>Completed</Trans>
                  </span>
                  <span>
                    {formatDateAndTimeShort(pathwayEnrolment.completed_at)}
                  </span>
                </div>

                {pathwayEnrolment.importRecord && (
                  <>
                    <div className="Workbook__detail__item">
                      <span className="Workbook__label">
                        <Trans>Imported</Trans>
                      </span>
                      <span>
                        {formatDateAndTimeShort(pathwayEnrolment.created_at)}
                      </span>
                    </div>

                    <div className="Workbook__detail__item">
                      <span className="Workbook__label">
                        <Trans>Imported by</Trans>
                      </span>
                      {pathwayEnrolment.importRecord?.importedBy ? (
                        <>
                          <UserAvatar
                            user={
                              pathwayEnrolment.importRecord?.importedBy as any
                            }
                          />
                          {
                            pathwayEnrolment.importRecord?.importedBy
                              .display_name
                          }
                        </>
                      ) : (
                        <Trans>Self shared</Trans>
                      )}
                    </div>
                    <div className="Workbook__detail__item">
                      <span className="Workbook__label">
                        <Trans>Source</Trans>
                      </span>
                      <span>
                        {pathwayEnrolment.importRecord?.source_name ? (
                          pathwayEnrolment.importRecord?.source_name
                        ) : (
                          <Trans>N/A</Trans>
                        )}
                      </span>
                    </div>
                  </>
                )}

                {!pathwayEnrolment.importRecord?.importedBy && (
                  <div className="Workbook__detail__item">
                    <span className="Workbook__label">
                      <Trans>Shared by</Trans>
                    </span>
                    {pathwayEnrolment.sharedBy ? (
                      <>
                        <UserAvatar user={pathwayEnrolment.sharedBy} />
                        {pathwayEnrolment.sharedBy.display_name}
                      </>
                    ) : (
                      <Trans>Self shared</Trans>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </BorderedBox>
      </div>
      {renderOverviewTable()}
    </div>
  )
}

const renderHeaderStatus = (status: PathwayEnrolmentStatusEnum) => {
  switch (status) {
    case PathwayEnrolmentStatusEnum.COMPLETED:
      return <Trans>completed</Trans>
    case PathwayEnrolmentStatusEnum.FAILED:
      return <Trans>failed</Trans>
    case PathwayEnrolmentStatusEnum.IN_PROGRESS:
      return <Trans>completing</Trans>
    case PathwayEnrolmentStatusEnum.NOT_STARTED:
      return <Trans>has not started</Trans>
    case PathwayEnrolmentStatusEnum.OVERDUE:
      return <Trans>did not complete in target time</Trans>
  }
}

export default PathwayEnrolmentDetailAnalytics
